import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { client } from '../lib/client';

interface User {
  id: string;
  email: string;
  name?: string;
}

interface Session {
  user: User;
  access_token?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshSession: (opts?: { background?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  apiClient: (url: string, options?: RequestInit) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async (opts?: { background?: boolean }) => {
    try {
      // Only show the full-screen loading state on the very first,
      // mount-time session check. A background refresh (e.g. the
      // window-focus listener below) must NOT flip isLoading to true —
      // doing so causes AppLayout to unmount/remount the entire app
      // (including TwoFactorGate) every time the window regains focus,
      // which was interrupting in-progress user actions like the
      // attachment-download click.
      if (!opts?.background) {
        setIsLoading(true);
      }
      console.log("AuthContext: Fetching session...");
      const result = await client.auth.getSession();
      console.log("AuthContext: Session result:", result);
      
      // Handle different response formats from better-auth
      const sessionData = result?.data || result;
      const userData = sessionData?.user;
      
      if (userData?.id) {
        console.log("AuthContext: User found:", userData.email);
        setSession({
          user: {
            id: userData.id,
            email: userData.email,
            name: userData.name,
          },
          access_token: sessionData?.session?.token || sessionData?.access_token,
        });
      } else {
        console.log("AuthContext: No user in session");
        setSession(null);
      }
    } catch (error) {
      console.error("AuthContext: Error fetching session", error);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Note: client.auth has no onAuthStateChange — the minimal client
    // shim (src/lib/client.ts) only implements getSession/signOut, so
    // there is no live event stream to subscribe to here. Session state
    // is refreshed on mount, after login/logout (callers invoke
    // refreshSession() directly), and on window focus below.
    refreshSession();

    const onFocus = () => refreshSession({ background: true });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshSession]);

  const apiClient = useCallback(async (url: string, options?: RequestInit) => {
    try {
      // Build fetch options - only pass options when provided to avoid overriding EdgeSpark auth headers
      let fetchOptions: RequestInit | undefined;
      if (options) {
        fetchOptions = { ...options };
        // Add Content-Type for requests with body if not already set
        if (fetchOptions.body && !fetchOptions.headers) {
          fetchOptions.headers = { 'Content-Type': 'application/json' };
        }
      }

      const response = await client.api.fetch(url, fetchOptions);
      
      if (!response.ok) {
        console.error(`apiClient: ${url} returned ${response.status}`);
        // Parse error response body for detailed governance/permission error messages
        try {
          const errorBody = await response.json();
          const errorMsg = errorBody?.governance?.reasonMessage || errorBody?.message || errorBody?.error || `HTTP ${response.status}`;
          return { data: null, error: errorMsg, governance: errorBody?.governance || null };
        } catch {
          return { data: null, error: `HTTP ${response.status}` };
        }
      }

      const json = await response.json();
      return json;
    } catch (error) {
      console.error(`apiClient error for ${url}:`, error);
      return { data: null, error: 'Request failed' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.auth.signOut();
    } catch (error) {
      console.error("AuthContext: Error signing out", error);
    } finally {
      setSession(null);
    }
  }, []);

  const value: AuthContextType = {
    user: session?.user ?? null,
    session,
    isLoading,
    isAuthenticated: !!session?.user?.id,
    refreshSession,
    logout,
    apiClient,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
